import { useEffect, useState } from 'react'
import { Alert, Anchor, Button, Group, Loader, Modal, Stack, Tabs, Text, TextInput } from '@mantine/core'
import { IconArrowUp, IconPlayerPlay, IconRefresh, IconTrash } from '@tabler/icons-react'
import { DataCard } from '@/components/ui'
import type { GatewayDetalhe, JobDoGateway, OrgComAcesso } from '@/api/whatsappGateways'
import { EM_CURSO, useAtualizarGateway, useChavesDaOrg, useGateway, useReiniciarGateway, useRemoverGateway, useRenomearGateway, useTentarDeNovo } from '@/hooks/useWhatsappGateways'
import { AtendimentoHumanoDoGateway } from './AtendimentoHumanoDoGateway'
import { ChavesDoGateway } from './ChavesDoGateway'
import { EstadoAtualDoGateway } from './EstadoAtualDoGateway'
import { LinhaDoTempoDoJob } from './LinhaDoTempoDoJob'
import { OperacoesDoGateway } from './OperacoesDoGateway'
import { ROTULO_STATUS_DO_JOB, VERBO_DO_KIND, partesDoErro, passoDeRetomada, rotuloDoPasso } from './passosDoJob'
import { mensagemDeErro } from './mensagens'
import { PareamentoDoGateway } from './PareamentoDoGateway'
import { RotuloDeEstado } from './RotuloDeEstado'
import { TrilhaDoGateway } from './TrilhaDoGateway'
import { VersaoDoGateway } from './VersaoDoGateway'

type Aba = 'estado' | 'operacoes' | 'trilha'

/**
 * A linha do tempo do último job só É o estado do gateway enquanto o job anda, ou quando
 * a falha dele deixou o gateway em `falhou`. Job falho com o gateway de volta ao que era
 * (ex.: remoção recusada no `sync_remover`, consoleai#25885) não mudou nada: vira uma
 * linha, e o histórico fica na aba "Operações".
 */
export function linhaDoTempoEhOEstado(g: Pick<GatewayDetalhe, 'status'>, job: JobDoGateway): boolean {
  return job.status === 'pending' || job.status === 'running' || (job.status === 'failed' && g.status === 'falhou')
}

function diaEHora(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function TentativaRecusada({ job, onVerOperacoes }: { job: JobDoGateway; onVerOperacoes: () => void }) {
  const erro = partesDoErro(job.last_error)
  const motivo = erro ? `: ${erro.frase ?? erro.codigo}` : ''
  return (
    <Text size="sm" c="dimmed" data-testid="tentativa-recusada">
      Tentativa de {VERBO_DO_KIND[job.kind]} em {diaEHora(job.finished_at ?? job.updated_at)} foi recusada{motivo}. Nada foi alterado.{' '}
      <Anchor component="button" type="button" size="sm" onClick={onVerOperacoes}>Ver em Operações</Anchor>
    </Text>
  )
}

type Acao = 'remover' | 'reiniciar' | 'atualizar' | 'tentar'
const CONFIRMACAO: Record<Exclude<Acao, 'tentar'>, { titulo: string; texto: string }> = {
  remover: {
    titulo: 'Excluir o gateway?',
    texto: 'O serviço sai do ECS e a sessão sai de todo caminho montado; o id não volta a ser usado. Chave de API apontada para ele segura a remoção até ser desapontada.',
  },
  atualizar: {
    titulo: 'Atualizar o gateway para a versão corrente?',
    texto: 'O ECS troca a tarefa pela versão corrente. O WhatsApp deste gateway fica fora por alguns minutos e a sessão pareada é mantida; se a tarefa nova não subir, o ECS volta para a versão anterior.',
  },
  reiniciar: {
    titulo: 'Reiniciar o gateway?',
    texto: 'O ECS sobe uma tarefa nova com a mesma versão. O WhatsApp deste gateway fica fora até ela subir; a sessão pareada é mantida.',
  },
}

/**
 * Detalhe de um gateway (spec §4/§4.1) em três abas: "Estado" (o que vale agora, as
 * ações de gestão, pareamento e chaves de API), "Operações" (todo job, com os passos de
 * cada um) e "Trilha".
 *
 * `PareamentoDoGateway` só monta com `org.pode.pair` E gateway `provisionado`
 * (decisão do controlador): sem sessão pronta não há QR para gerar. As ações
 * de gestão (renomear/reiniciar/excluir) só aparecem com `org.pode.manage`.
 */
export function DetalheDoGateway({ org, gwId, onRemovido }: { org: OrgComAcesso; gwId: string; onRemovido: () => void }) {
  const gw = useGateway(org.id, gwId)
  const renomear = useRenomearGateway(org.id, gwId)
  const remover = useRemoverGateway(org.id, gwId)
  const reiniciar = useReiniciarGateway(org.id, gwId)
  const atualizar = useAtualizarGateway(org.id, gwId)
  const tentar = useTentarDeNovo(org.id, gwId)
  // Mesma query de `ChavesDoGateway` (cache compartilhado): a confirmação de excluir diz
  // quais chaves seguram a remoção — o backend recusa com 409 `gateway_com_chaves`.
  const chaves = useChavesDaOrg(org.id)
  const apontadas = (chaves.data ?? []).filter((k) => k.gateway_id === gwId)
  const [nome, setNome] = useState<string | null>(null)
  const [confirmar, setConfirmar] = useState<Acao | null>(null)
  const [aba, setAba] = useState<Aba>('estado')

  // Se o gateway virar `removido` sob o polling (job de remoção terminando por conta
  // própria, ou outra sessão removendo), a seleção fica apontando para uma tela sem
  // ação possível. `onRemovido` já é o sinal que a lista usa para limpar `gwId`.
  const statusAtual = gw.data?.status
  useEffect(() => {
    if (statusAtual === 'removido') onRemovido()
  }, [statusAtual, onRemovido])

  if (gw.isError) return <Alert color="red" variant="light">{mensagemDeErro(gw.error)}</Alert>
  if (gw.isLoading || !gw.data) return <Loader size="sm" />
  const g = gw.data
  const pronto = g.status === 'provisionado'
  const emCurso = EM_CURSO.has(g.status)
  // Mesma regra de `registry.pedir_atualizacao`: provisionado fora da versão, ou a
  // atualização anterior falhou. O backend recusa igual (409 gateway_nao_atualizavel).
  const desatualizado = g.image_tag !== g.versao_corrente
  const atualizavel = org.pode.manage && ((pronto && desatualizado) || (g.status === 'falhou' && g.job?.kind === 'update'))
  // Mesma regra da rota `…/retry` (spec §4.4): só o provisionamento falho que é o último job.
  const retomavel = org.pode.manage && g.status === 'falhou' && g.job?.kind === 'provision' && g.job.status === 'failed'
  const falhouAoRemover = g.status === 'falhou' && g.job?.kind === 'deprovision'
  const retomaDe = g.job ? rotuloDoPasso('provision', passoDeRetomada(g.job.step, desatualizado)) : ''
  const confirmacao = confirmar === 'tentar'
    ? { titulo: 'Tentar de novo?', texto: `O provisionamento recomeça de "${retomaDe}", num job novo. Os passos já feitos não se refazem do zero: cada um confere o que já existe na AWS.` }
    : confirmar ? CONFIRMACAO[confirmar] : null
  const mutavel = org.pode.manage && !emCurso && g.status !== 'removido'
  const nomeDigitado = (nome ?? g.name).trim()

  function confirmarAcao() {
    if (confirmar === 'remover') remover.mutate(undefined, { onSuccess: () => { setConfirmar(null); onRemovido() } })
    if (confirmar === 'reiniciar') reiniciar.mutate(undefined, { onSuccess: () => setConfirmar(null) })
    if (confirmar === 'atualizar') atualizar.mutate(undefined, { onSuccess: () => setConfirmar(null) })
    if (confirmar === 'tentar') tentar.mutate(undefined, { onSuccess: () => setConfirmar(null) })
  }

  return (
    <DataCard title={g.name}>
      <Stack gap="md">
        <Group gap="sm">
          <RotuloDeEstado status={g.status} kindDoUltimoJob={g.job?.kind} />
          <Text ff="monospace" size="xs">{g.id}</Text>
          <VersaoDoGateway tag={g.image_tag} corrente={g.versao_corrente} />
        </Group>
        <Tabs value={aba} onChange={(v) => setAba((v as Aba | null) ?? 'estado')} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="estado">Estado</Tabs.Tab>
            <Tabs.Tab value="operacoes">Operações</Tabs.Tab>
            <Tabs.Tab value="trilha">Trilha</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="estado" pt="md">
            <Stack gap="md">
              {g.status !== 'removido' && <EstadoAtualDoGateway orgId={org.id} gwId={g.id} podeParear={org.pode.pair} />}
              {/* A seção decide sozinha por `pode.atender` do WhatsApi da organização (spec conversas §5.6). */}
              {g.status !== 'removido' && <AtendimentoHumanoDoGateway orgId={org.id} gwId={g.id} />}
              {g.job && (linhaDoTempoEhOEstado(g, g.job) ? (g.job.passos?.length ? (
                <LinhaDoTempoDoJob
                  job={g.job}
                  acaoNoPassoFalho={retomavel ? (
                    <Button size="xs" variant="light" leftSection={<IconPlayerPlay size={14} />} onClick={() => setConfirmar('tentar')}>
                      Tentar de novo
                    </Button>
                  ) : null}
                />
              ) : (
                <>
                  {/* Job anterior à linha do tempo (MIGRATION_393): a linha única de antes. */}
                  <Text size="sm">
                    Última operação: {VERBO_DO_KIND[g.job.kind]} — {ROTULO_STATUS_DO_JOB[g.job.status]}{g.job.step ? ` (passo ${g.job.step})` : ''}
                  </Text>
                  {g.job.last_error && <Alert color="red" variant="light" title="Último erro">{g.job.last_error}</Alert>}
                </>
              )) : g.job.status === 'failed' ? (
                <TentativaRecusada job={g.job} onVerOperacoes={() => setAba('operacoes')} />
              ) : null)}
              {mutavel && (
                <Group align="flex-end">
                  <TextInput label="Nome" value={nome ?? g.name} maxLength={80} onChange={(e) => setNome(e.currentTarget.value)} />
                  <Button variant="outline" disabled={!nomeDigitado || nomeDigitado === g.name} loading={renomear.isPending}
                    onClick={() => renomear.mutate(nomeDigitado, { onSuccess: () => setNome(null) })}>
                    Renomear
                  </Button>
                </Group>
              )}
              {mutavel && (
                <Group>
                  {atualizavel && (
                    <Button leftSection={<IconArrowUp size={16} />} onClick={() => setConfirmar('atualizar')}>Atualizar agora</Button>
                  )}
                  {pronto && (
                    <Button variant="outline" leftSection={<IconRefresh size={16} />} onClick={() => setConfirmar('reiniciar')}>Reiniciar</Button>
                  )}
                  <Button color="red" leftSection={<IconTrash size={16} />} onClick={() => setConfirmar('remover')}>
                    {falhouAoRemover ? 'Excluir de novo' : 'Excluir'}
                  </Button>
                </Group>
              )}
              {org.pode.pair && pronto && <PareamentoDoGateway orgId={org.id} gwId={g.id} />}
              {org.pode.pair && !pronto && g.status !== 'removido' && (
                <Text size="sm" c="dimmed">O QR fica disponível quando o gateway terminar de subir.</Text>
              )}
              <ChavesDoGateway org={org} gateway={g} />
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="operacoes" pt="md">
            <OperacoesDoGateway orgId={org.id} gwId={g.id} />
          </Tabs.Panel>
          <Tabs.Panel value="trilha" pt="md">
            <TrilhaDoGateway orgId={org.id} gwId={g.id} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
      <Modal opened={confirmar != null} onClose={() => setConfirmar(null)} title={confirmacao?.titulo ?? ''}>
        <Stack>
          <Text size="sm">{confirmacao?.texto ?? ''}</Text>
          {confirmar === 'remover' && apontadas.length > 0 && (
            <Alert color="yellow" variant="light" title={apontadas.length === 1 ? 'Chave apontada para este gateway' : 'Chaves apontadas para este gateway'}>
              <Text size="sm">
                {apontadas.length === 1
                  ? `A chave "${apontadas[0].name}" aponta para este gateway e impede a exclusão. Desaponte ou exclua essa chave antes.`
                  : `As chaves ${apontadas.map((k) => `"${k.name}"`).join(', ')} apontam para este gateway e impedem a exclusão. Desaponte ou exclua cada uma antes.`}
              </Text>
            </Alert>
          )}
          <Group>
            <Button variant="subtle" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button color={confirmar === 'remover' ? 'red' : undefined} loading={remover.isPending || reiniciar.isPending || atualizar.isPending || tentar.isPending} onClick={confirmarAcao}>
              Confirmar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </DataCard>
  )
}
